import { type FC, useEffect, useLayoutEffect, useRef } from "react";
import { translate } from "lib/blocs/TranslateionBloc";
import type { TourStepDefinition } from "./types";
import { DEFAULT_PLACEMENT } from "./types";
import {
  VIEWPORT_SAFE_MARGIN_PX,
  SLIDE_ENTER,
  SLIDE_EXIT,
  SPRING_ENTER,
  QUICK_EXIT
} from "./constants";
import {
  TooltipContainer,
  TooltipCard,
  TooltipStepCounter,
  TooltipText,
  TooltipFooter,
  TooltipButton
} from "./tourGuide.styles";

interface TourGuideTooltipProps {
  step: TourStepDefinition;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  layoutReady: boolean;
}

const STEP_TEXT_ID = "tour-guide-step-text";

function clampToViewport(
  anchorName: string,
  container: HTMLElement
): number | null {
  const anchor = document.querySelector<HTMLElement>(
    `[data-tour-anchor="${anchorName}"]`
  );
  if (!anchor) {
    return null;
  }

  const anchorRect = anchor.getBoundingClientRect();
  const anchorCenter = anchorRect.left + anchorRect.width / 2;
  const tooltipWidth = container.offsetWidth;
  const vw = window.innerWidth;

  let left = anchorCenter - tooltipWidth / 2;
  left = Math.max(VIEWPORT_SAFE_MARGIN_PX, left);
  left = Math.min(vw - VIEWPORT_SAFE_MARGIN_PX - tooltipWidth, left);
  return left;
}

const TourGuideTooltip: FC<TourGuideTooltipProps> = ({
  step,
  stepIndex,
  totalSteps,
  onNext,
  layoutReady
}) => {
  const placement = step.placement ?? DEFAULT_PLACEMENT;
  const isLast = stepIndex === totalSteps - 1;
  const buttonLabel = step.buttonTextKey
    ? translate(step.buttonTextKey)
    : isLast
      ? translate("tourGuide.gotIt")
      : translate("tourGuide.next");

  const cardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const isHorizontal = placement === "top" || placement === "bottom";
    if (!isHorizontal || !containerRef.current) {
      return;
    }

    const left = clampToViewport(step.anchorName, containerRef.current);
    if (left != null) {
      containerRef.current.style.left = `${left}px`;
    }
  }, [step.anchorName, placement, stepIndex]);

  useEffect(() => {
    cardRef.current?.focus();
  }, [stepIndex]);

  return (
    <TooltipContainer
      ref={containerRef}
      anchorName={step.anchorName}
      placement={placement}
      role="dialog"
      aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
      aria-describedby={STEP_TEXT_ID}
      initial={{ opacity: 0, ...SLIDE_ENTER[placement] }}
      animate={
        layoutReady
          ? { opacity: 1, x: 0, y: 0, transition: SPRING_ENTER }
          : { opacity: 0, ...SLIDE_ENTER[placement] }
      }
      exit={{
        opacity: 0,
        ...SLIDE_EXIT[placement],
        transition: QUICK_EXIT
      }}
    >
      <TooltipCard
        ref={cardRef}
        tabIndex={0}
        role="button"
        onClick={onNext}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onNext();
          }
        }}
      >
        <TooltipText id={STEP_TEXT_ID}>
          {translate(step.textKey, step.textVars)}
        </TooltipText>
        <TooltipFooter>
          <TooltipStepCounter aria-hidden="true">
            {stepIndex + 1}/{totalSteps}
          </TooltipStepCounter>
          <TooltipButton aria-hidden="true" tabIndex={-1}>
            {buttonLabel}
          </TooltipButton>
        </TooltipFooter>
      </TooltipCard>
    </TooltipContainer>
  );
};

export default TourGuideTooltip;
